import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Info,
  Loader2,
  Play,
  RefreshCw,
  RotateCcw,
  Search,
  Square,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useCancelPrismImport,
  useClearPrismSession,
  usePrismImportJob,
  usePrismImportJobs,
  usePrismSession,
  usePrismWipe,
  usePrismWipePreview,
  useSavePrismSession,
  useImportCatalogEvent,
  prismImportKeys,
  usePrismCatalog,
  useScanPrismCatalog,
  useSetPrismCatalogVerified,
  useStartPrismImport,
} from '@/hooks/usePrismImport'
import { requestErrorMessage } from '@/helpers/apiClient'
import { prismImportWhenSchema, type PrismImportWhen } from '@lib/dto/prism-import.dto'
import { parsePrismEventRefs } from '@lib/helpers/prism-event-ref'
import {
  savePrismSessionInputSchema,
  startPrismImportInputSchema,
  type StartPrismImportInput,
} from '@lib/input/prism-import.input'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Tooltip } from '@/components/ui/tooltip'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pagination } from '@/components/ui/pagination'
import { serverPage } from '@/lib/pagination'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast'
import type { PrismCatalogEventDto, PrismImportJobDto, PrismWipeCounts } from '@lib/dto/prism-import.dto'
import type { PrismImportJobStatus } from '@lib/enums/prism-import-job-status.enum'
import type { BadgeTone } from '@/components/ui/badge'
import { FormError } from '@/components/ui/form-error'
import { formatEventDate } from '@/lib/eventTime'
import { Card } from '@/components/ui/card'

/**
 * Events that came in with floor plans and no furniture. Not a success, and said
 * loudly: these used to show as imported, and the blank plans were found later.
 */
function emptyPlansNote(count: number | undefined): React.ReactNode {
  if (count === undefined || count === 0) return null
  return (
    <span className="font-medium text-danger-ink">
      {` · ${String(count)} imported without furniture — see the ✗ lines in the log`}
    </span>
  )
}

const STATUS_TONE: Record<PrismImportJobStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'warning',
}

/** How a job's status reads on the badge. The stored value keeps its own spelling (`cancelled`). */
const STATUS_LABEL: Record<PrismImportJobStatus, string> = {
  queued: 'queued',
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
  cancelled: 'canceled',
}

/** The word that has to be typed before anything is cleared. */
const RESET_WORD = 'WIPE'

/**
 * What a reset clears, in the order it reads. Each line says what goes with the
 * thing named, because that is the part nobody expects: an event takes its plans,
 * a space takes its background, a piece takes the stock counted against it.
 */
const RESET_ROWS: Array<{ key: keyof PrismWipeCounts; label: string; also: string }> = [
  { key: 'events', label: 'Events', also: 'with their plans, documents, run of the day and rentals' },
  { key: 'venueSpaces', label: 'Venue spaces', also: 'with their floor backgrounds and saved layouts' },
  { key: 'inventoryVersions', label: 'Inventory versions', also: 'with every stock count in them' },
  { key: 'clientUsers', label: 'Client accounts', also: 'invited or active' },
  { key: 'furnitureItems', label: 'Furniture', also: 'the whole catalog, and what clients brought' },
  { key: 'furnitureGroups', label: 'Arrangements', also: 'the group library' },
]

/**
 * Which database this is about. The API cannot always name it — a local machine
 * sets nothing — and naming it "unknown" reads worse than not naming it, when the
 * whole point of saying it is to stop someone clearing the wrong one.
 */
function environmentLabel(environment: string | undefined): string {
  if (!environment || environment === 'unknown') return 'this environment'
  return environment
}

/** "3 events, 1 venue space and 12 client accounts", or null when nothing went. */
function describeCounts(counts: PrismWipeCounts): string | null {
  const parts = RESET_ROWS.filter((row) => counts[row.key] > 0).map(
    (row) => `${String(counts[row.key])} ${row.label.toLowerCase()}`,
  )
  if (parts.length === 0) return null
  if (parts.length === 1) return parts[0] ?? null
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`
}

function WhenSelect({
  value,
  onChange,
}: {
  value: PrismImportWhen
  onChange: (value: PrismImportWhen) => void
}) {
  return (
    <Field label="When" htmlFor="when">
      <Select
        id="when"
        value={value}
        onChange={(e) => {
          const parsed = prismImportWhenSchema.safeParse(e.target.value)
          if (parsed.success) onChange(parsed.data)
        }}
      >
        <option value="upcoming">Upcoming</option>
        <option value="past">Past</option>
        <option value="all">All</option>
      </Select>
    </Field>
  )
}

function JobLog({ jobId }: { jobId: string }) {
  const q = usePrismImportJob(jobId)
  const logRef = React.useRef<HTMLPreElement>(null)
  const log = q.data?.log ?? ''

  React.useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [log])

  if (q.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading log…
      </div>
    )
  }
  if (q.isError) {
    return <FormError>{requestErrorMessage(q.error)}</FormError>
  }
  const job = q.data
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={STATUS_TONE[job.status]}>{STATUS_LABEL[job.status]}</Badge>
        {job.result ? (
          <span className="text-ink-muted">
            processed {job.result.eventsProcessed} · ok {job.result.eventsSucceeded} · failed{' '}
            {job.result.eventsFailed}
            {emptyPlansNote(job.result.eventsWithEmptyPlans)}
            {job.result.clientsAdded != null ? ` · clients +${job.result.clientsAdded}` : ''}
            {job.result.documentsAdded != null ? ` · docs +${job.result.documentsAdded}` : ''}
            {job.result.eventsWithoutFloorplan != null && job.result.eventsWithoutFloorplan > 0
              ? ` · ${job.result.eventsWithoutFloorplan} without a floor plan`
              : ''}
            {job.result.eventsDeferred != null && job.result.eventsDeferred > 0
              ? ` · ${job.result.eventsDeferred} left for a later run`
              : ''}
            {job.result.eventsSkipped != null && job.result.eventsSkipped > 0
              ? ` · ${job.result.eventsSkipped} already imported`
              : ''}
            {job.result.eventsSkippedPast != null && job.result.eventsSkippedPast > 0
              ? ` · ${job.result.eventsSkippedPast} past`
              : ''}
          </span>
        ) : null}
      </div>
      {job.error ? <FormError>{job.error}</FormError> : null}
      <pre
        ref={logRef}
        className="max-h-80 overflow-auto rounded-md border border-line bg-surface-sunken p-3 font-mono text-xs leading-relaxed text-ink"
      >
        {log || '(no log yet)'}
      </pre>
    </div>
  )
}

function jobSelectionLabel(job: PrismImportJobDto): string {
  if (job.config.eventIds?.length) return ` · ${String(job.config.eventIds.length)} id(s)`
  if (job.config.venueName) return ` · venue ${job.config.venueName}`
  if (job.config.limit) {
    const page = job.config.page && job.config.page > 1 ? ` · page ${String(job.config.page)}` : ''
    return ` · limit ${String(job.config.limit)}${page}`
  }
  return ''
}

function JobRow({
  job,
  selected,
  onSelect,
  onCancel,
  canceling,
}: {
  job: PrismImportJobDto
  selected: boolean
  onSelect: () => void
  onCancel?: () => void
  canceling?: boolean
}) {
  const active = job.status === 'queued' || job.status === 'running'
  return (
    <div
      className={
        selected
          ? 'flex w-full items-start gap-1 rounded-md border border-line bg-surface-current px-1 py-1'
          : 'flex w-full items-start gap-1 rounded-md border border-transparent px-1 py-1 hover:bg-surface-hover'
      }
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 flex-col gap-1 px-2 py-1 text-left"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium">{job.id.slice(0, 8)}…</span>
          <Badge tone={STATUS_TONE[job.status]}>{STATUS_LABEL[job.status]}</Badge>
        </div>
        <span className="text-xs text-ink-muted">
          {new Date(job.createdAt).toLocaleString()} · {job.createdByName}
          {job.config.dryRun ? ' · dry-run' : ''}
          {jobSelectionLabel(job)}
        </span>
      </button>
      {active && onCancel ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="mt-0.5 shrink-0"
          pending={canceling}
        >
          <Square className="h-4 w-4" />
          Cancel
        </Button>
      ) : null}
    </div>
  )
}

/**
 * The Social Tables session the importer runs with.
 *
 * Social Tables verifies an unrecognized browser by emailing the account holder,
 * which a server cannot answer — so a person mints the session locally and hands
 * it over. It used to arrive as a deploy variable, which made every expiry a
 * deploy; here it is stored, and each successful run writes the refreshed cookies
 * back, so regular imports keep it alive. The session is never displayed: it is a
 * live sign-in for that account, so only its state is shown.
 */
function SessionPanel() {
  const sessionQuery = usePrismSession()
  const save = useSavePrismSession()
  const clear = useClearPrismSession()
  const [sessionValue, setSessionValue] = React.useState('')
  const [sessionSig, setSessionSig] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const session = sessionQuery.data

  const expiry = session?.expiresAt ? new Date(session.expiresAt) : null
  const expired = expiry !== null && expiry.getTime() < Date.now()
  const healthy = Boolean(session?.present && !expired)

  function sessionBadge(): React.ReactNode {
    if (!session) return null
    if (session.present) {
      return (
        <Badge tone={expired ? 'danger' : 'success'} dot>
          {expired ? 'expired' : 'stored'}
        </Badge>
      )
    }
    return (
      <Badge tone={session.envFallback ? 'info' : 'warning'} dot>
        {session.envFallback ? 'using PRISM_SESSION_STATE' : 'none'}
      </Badge>
    )
  }

  function statusLine(): React.ReactNode {
    if (session?.present) {
      return (
        <p className="text-sm text-ink-muted">
          {session.cookieCount != null ? `${session.cookieCount} cookies · ` : ''}
          {expiry ? `${expired ? 'expired' : 'expires'} ${expiry.toLocaleDateString()} · ` : ''}
          {session.lastUsedAt
            ? `last used ${new Date(session.lastUsedAt).toLocaleString()}`
            : 'not used by a run yet'}
          {session.updatedByName ? ` · pasted by ${session.updatedByName}` : ' · saved by the importer'}
          {session.teamId != null ? ` · team ${session.teamId}` : ''}
        </p>
      )
    }
    return (
      <p className="text-sm text-ink-muted">
        {session?.envFallback
          ? 'No session stored — the importer is falling back to the PRISM_SESSION_STATE variable. Paste the two cookies here to stop depending on a deploy.'
          : 'No session stored. The importer will try to sign in, which Social Tables answers with an emailed verification a server cannot read.'}
      </p>
    )
  }

  function editor(): React.ReactNode {
    const canSave = sessionValue.trim() !== '' && sessionSig.trim() !== '' && !save.isPending
    return (
      <>
        <p className="text-sm text-ink-muted">
          Sign in at home.socialtables.com, then DevTools → Application → Cookies →{' '}
          <span className="font-mono">.socialtables.com</span>. Copy the Value column for each cookie below.
          They are HttpOnly — they will not show in <span className="font-mono">document.cookie</span>.
        </p>
        <Field label="session" htmlFor="prism-session-cookie">
          <Input
            id="prism-session-cookie"
            value={sessionValue}
            onChange={(e) => setSessionValue(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            className="font-mono text-sm"
            placeholder="Value of the session cookie"
          />
        </Field>
        <Field label="session.sig" htmlFor="prism-session-sig">
          <Input
            id="prism-session-sig"
            value={sessionSig}
            onChange={(e) => setSessionSig(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            className="font-mono text-sm"
            placeholder="Value of the session.sig cookie"
          />
        </Field>
        {error ? <FormError>{error}</FormError> : null}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            disabled={!canSave}
            onClick={() => {
              setError(null)
              const parsed = savePrismSessionInputSchema.safeParse({
                session: sessionValue,
                sessionSig,
              })
              if (!parsed.success) {
                setError(parsed.error.issues.map((issue) => issue.message).join(' '))
                return
              }
              save.mutate(parsed.data, {
                onSuccess: () => {
                  setSessionValue('')
                  setSessionSig('')
                },
                onError: (e) => setError(requestErrorMessage(e)),
              })
            }}
            pending={save.isPending}
          >
            Save session
          </Button>
          {session?.present ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null)
                clear.mutate(undefined, { onError: (e) => setError(requestErrorMessage(e)) })
              }}
              pending={clear.isPending}
            >
              Forget it
            </Button>
          ) : null}
        </div>
      </>
    )
  }

  if (sessionQuery.isPending) {
    return (
      <Card asChild className="space-y-3 p-4">
        <section>
          <h2 className="text-sm font-semibold">Social Tables session</h2>
          <p className="flex items-center gap-2 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking session…
          </p>
        </section>
      </Card>
    )
  }

  if (healthy) {
    return (
      <details className="group rounded-md border border-line p-4 open:space-y-3">
        <summary
          className={cn(
            'flex cursor-pointer list-none items-center justify-between gap-2',
            '[&::-webkit-details-marker]:hidden',
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <h2 className="text-sm font-semibold">Social Tables session</h2>
            {sessionBadge()}
          </span>
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs text-ink-muted">
              {expiry ? `expires ${expiry.toLocaleDateString()}` : 'valid'}
              {session?.lastUsedAt ? ` · last used ${new Date(session.lastUsedAt).toLocaleString()}` : ''}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted transition-transform duration-150 ease-out group-open:rotate-180" />
          </span>
        </summary>
        <div className="space-y-3 pt-3">
          {statusLine()}
          {editor()}
        </div>
      </details>
    )
  }

  return (
    <Card asChild className="space-y-3 p-4">
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Social Tables session</h2>
          {sessionBadge()}
        </div>
        {sessionQuery.isError ? (
          <FormError>{requestErrorMessage(sessionQuery.error)}</FormError>
        ) : null}
        {statusLine()}
        {editor()}
      </section>
    </Card>
  )
}

/**
 * Clear this organization's imported data so an import can start from a known
 * state.
 *
 * A part-imported organization is hard to reason about — half its events on the
 * wrong venue, spaces nobody meant to keep — and putting that right one record at
 * a time is worse than starting again. So this exists, and because it cannot be
 * undone it says what will go, in counts read at the moment it is opened, names
 * the environment it would run in, and asks for the word to be typed.
 */
function ResetPanel() {
  const [open, setOpen] = React.useState(false)
  const [typed, setTyped] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const preview = usePrismWipePreview(open)
  const reset = usePrismWipe()
  const { toast } = useToast()
  const counts = preview.data?.counts
  const nothingToClear = counts != null && RESET_ROWS.every((row) => counts[row.key] === 0)

  function close(): void {
    setOpen(false)
    setTyped('')
    setError(null)
  }

  return (
    // A panel like any other, not a red one: the danger is in the action, and a
    // standing wash of red reads as an error that is already happening. The
    // trigger is drawn as "Delete event" is; the filled red button is the one
    // that confirms, in the dialog.
    <Card asChild className="space-y-3 p-4">
      <section>
        <h2 className="text-sm font-semibold">Start over</h2>
        <p className="text-sm text-ink-muted">
          Clear every event, venue space, stock count, client account and furniture piece in this organization —
          imported or made by hand in CSE — so an import can run against a clean database. Venues, staff accounts
          and vendors are kept. This cannot be undone.
        </p>
        <Button
          type="button"
          variant="outline"
          className="text-danger-ink hover:bg-danger-soft hover:text-danger-ink"
          onClick={() => setOpen(true)}
        >
          <Trash2 className="h-4 w-4" />
          Wipe this organization’s data
        </Button>

        <Dialog
          open={open}
          onOpenChange={(next) => {
            if (next) setOpen(true)
            else close()
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Wipe this organization’s data?</DialogTitle>
              <DialogDescription>
                This deletes the records below from {environmentLabel(preview.data?.environment)}, whether they
                came from an import or were made by hand, and cannot be undone.
              </DialogDescription>
            </DialogHeader>

            {preview.isPending ? (
              <p className="flex items-center gap-2 text-sm text-ink-muted">
                <Loader2 className="h-4 w-4 animate-spin" /> Counting what would go…
              </p>
            ) : null}
            {preview.isError ? (
              <FormError>{requestErrorMessage(preview.error)}</FormError>
            ) : null}
            {counts ? (
              <div className="space-y-2 rounded-[var(--radius-control)] border border-line p-3">
                <ul className="space-y-1.5">
                  {RESET_ROWS.map((row) => (
                    <li key={row.key} className="flex items-start justify-between gap-3 text-sm">
                      <span className="min-w-0">
                        <span className={counts[row.key] === 0 ? 'text-ink-muted' : ''}>{row.label}</span>
                        <span className="block text-xs text-ink-muted">{row.also}</span>
                      </span>
                      <span className="shrink-0 font-medium tabular-nums">{counts[row.key]}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-ink-muted">
                  {preview.data?.venuesKept ?? 0} venue{(preview.data?.venuesKept ?? 0) === 1 ? '' : 's'} kept,
                  along with their tracks, staff accounts and vendors.
                </p>
              </div>
            ) : null}

            <Field
              label={`Type ${RESET_WORD} to confirm`}
              htmlFor="reset-confirm"
              hint={nothingToClear ? 'There is nothing to clear.' : undefined}
            >
              <Input
                id="reset-confirm"
                value={typed}
                autoComplete="off"
                onChange={(e) => setTyped(e.target.value)}
                placeholder={RESET_WORD}
              />
            </Field>
            {error ? <FormError>{error}</FormError> : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={typed !== RESET_WORD || reset.isPending || preview.isPending}
                onClick={() => {
                  setError(null)
                  reset.mutate(
                    { confirm: RESET_WORD },
                    {
                      onSuccess: (result) => {
                        const summary = describeCounts(result.counts)
                        toast(summary ? `Wiped ${summary}.` : 'There was nothing to wipe.', {
                          variant: 'warning',
                        })
                        close()
                      },
                      onError: (e) => setError(requestErrorMessage(e)),
                    },
                  )
                }}
                pending={reset.isPending}
              >
                Wipe it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </Card>
  )
}

/**
 * How to show what Social Tables calls the event's owner.
 *
 * It is the venue for a venue-owned event and a person for a planner-owned one,
 * and nothing reliably tells the two apart from the name: "Becca Lerum" is a
 * manager that reads as a venue, "The Commons" is a venue that reads as a person.
 *
 * So the venue comes from a source that knows it — CSE once the event is imported,
 * Prismm's occasion record before that — and when neither has one the owner is
 * shown as exactly what it is, Social Tables' owner, claiming nothing about whether
 * that is a place or a person.
 */
function ownerNote(ev: PrismCatalogEventDto): string | null {
  const owner = ev.ownerName?.trim()
  if (!owner || owner === ev.venueName) return null
  return `Social Tables owner: ${owner}`
}

function formatStDate(startEpoch: number | null): string {
  if (startEpoch == null) return 'No date'
  return new Date(startEpoch).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatIsoDate(iso: string | null): string {
  if (!iso) return 'No date'
  const ms = new Date(iso).getTime()
  return Number.isFinite(ms) ? formatStDate(ms) : 'No date'
}

/** Where one catalog row stands, as a badge. */
function catalogStatus(ev: PrismCatalogEventDto): { label: string; tone: BadgeTone } {
  // Skipped before "not imported": both are absent from CSE, but one of them was
  // a decision an import made and can be acted on, and the other is just a row
  // nobody has got to yet. Reading the same for both is what hid nineteen events.
  if (!ev.imported && ev.skippedAt != null) return { label: 'Skipped', tone: 'warning' }
  if (!ev.imported) return { label: 'Not imported', tone: 'neutral' }
  if (ev.verified) return { label: 'Verified', tone: 'success' }
  return { label: 'Imported', tone: 'info' }
}

/** One row: what Social Tables says, what CSE has, and what can be done about it. */
function CatalogRow({
  ev,
  importing,
  onJobStarted,
}: {
  ev: PrismCatalogEventDto
  /** An import job for this event is queued or running. */
  importing: boolean
  onJobStarted: (id: string) => void
}): React.ReactElement {
  const setVerified = useSetPrismCatalogVerified()
  const runImport = useImportCatalogEvent()
  const { toast } = useToast()
  const [confirmingReimport, setConfirmingReimport] = React.useState(false)
  const status = catalogStatus(ev)
  // In flight covers the whole import, not just the request that starts it.
  // Queuing the job takes a moment and the job itself takes minutes, so a button
  // that only waited on the request went live again while the import was still
  // going — with the row still reading "Not imported", which invites a second
  // click and a second import of the same event.
  const inFlight = runImport.isPending || importing
  const busy = setVerified.isPending || inFlight

  function onToggleVerified(next: boolean): void {
    setVerified.mutate(
      { catalogId: ev.id, verified: next },
      { onError: (e) => toast(requestErrorMessage(e), { variant: 'error' }) },
    )
  }

  function onRun(reimport: boolean): void {
    runImport.mutate(
      { catalogId: ev.id, reimport },
      {
        onSuccess: (job) => onJobStarted(job.id),
        onError: (e) => toast(requestErrorMessage(e), { variant: 'error' }),
      },
    )
  }

  return (
    <TableRow>
      <TableCell>
        {/* Imported, so there is a plan to look at: the name opens it. Checking
            the plan is what verifying means, and this is a worklist someone
            works down — so it opens in its own tab and the list, and whatever
            was filtered, is still here to come back to. */}
        {ev.eventId ? (
          <Link
            to={`/events/${ev.eventId}/plan`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-ink underline decoration-dotted underline-offset-4 hover:decoration-solid"
          >
            {ev.name}
          </Link>
        ) : (
          <span className="font-medium text-ink">{ev.name}</span>
        )}
        {ev.venueName ? (
          <span className="block text-xs text-ink-muted">{ev.venueName}</span>
        ) : ownerNote(ev) ? (
          <span className="block text-xs italic text-ink-muted">{ownerNote(ev)}</span>
        ) : null}
      </TableCell>
      <TableCell className="whitespace-nowrap text-ink-muted">
        {ev.startsAt ? formatEventDate(ev.startsAt) : 'No date'}
      </TableCell>
      <TableCell>
        <span className="flex items-center gap-1.5">
          <Badge tone={status.tone}>{status.label}</Badge>
          {/* Imported is one word for two quite different outcomes. An event brings
              its dates, clients and documents across whether or not a floor plan
              came with it, and the row read the same either way — so a plan that
              never arrived looked like a finished import until someone opened it. */}
          {ev.importedWithoutFloorPlan ? (
            <Tooltip content="Imported, but no floor plan came with it. Import again to try for one.">
              <AlertTriangle
                aria-label="No floor plan"
                className="h-4 w-4 shrink-0 text-hold-ink"
              />
            </Tooltip>
          ) : null}
          {/* Why it was passed over, in the import's own words. An event whose
              venue the source will not name has nowhere to go, and the reason is
              the only thing that says what would change that. */}
          {!ev.imported && ev.skippedReason != null ? (
            <Tooltip content={ev.skippedReason}>
              <Info aria-label="Why this was skipped" className="h-4 w-4 shrink-0 text-hold-ink" />
            </Tooltip>
          ) : null}
        </span>
      </TableCell>
      <TableCell>
        {ev.imported ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={ev.verified}
              disabled={busy}
              onChange={(e) => onToggleVerified(e.target.checked)}
            />
            <span className="text-ink-muted">
              {ev.verified
                ? `Accurate${ev.verifiedByName ? ` · ${ev.verifiedByName}` : ''}`
                : 'Accurate'}
            </span>
          </label>
        ) : (
          <span className="text-xs text-ink-muted">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-wrap justify-end gap-2">
          {!ev.imported ? (
            <Button type="button" size="sm" onClick={() => onRun(false)} disabled={busy}>
              {inFlight ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing…
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Import
                </>
              )}
            </Button>
          ) : null}
          {ev.imported && !ev.verified ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setConfirmingReimport(true)}
              disabled={busy}
            >
              {inFlight ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Re-importing…
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4" />
                  Re-import from scratch
                </>
              )}
            </Button>
          ) : null}
          {/* Asked first, and it says exactly when the current event goes: only once
              the new one has its furniture. It used to be one click, and the delete
              came before the import had even started. */}
          <ConfirmDialog
            open={confirmingReimport}
            onOpenChange={setConfirmingReimport}
            title={`Re-import “${ev.name}” from scratch?`}
            description={
              'Imports it again from Social Tables as a new event. The current event and its plans are ' +
              'replaced only once the new one has come in with its furniture. If the import fails or ' +
              'finds no furniture, the current event stays exactly as it is. Changes made to its plans ' +
              'in CSE are not carried over.'
            }
            confirmLabel="Re-import"
            busy={inFlight}
            onConfirm={() => {
              setConfirmingReimport(false)
              onRun(true)
            }}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

/**
 * The kept list of Social Tables events, and where the import stands on each.
 *
 * A scan appends to the list rather than rebuilding it, which is what lets this
 * be a worklist: the old crawl showed only what CSE was missing, so an event
 * disappeared the moment it was imported and there was nowhere to record that
 * someone had opened its plan and found it right. Verifying is a person's job and
 * only a person can tick it; a re-import clears the tick, because the plan it
 * spoke for is gone.
 */
function CatalogPanel({ onJobStarted }: { onJobStarted: (id: string) => void }): React.ReactElement {
  // Shares the jobs query the page already polls, so this costs no extra request
  // and only tells the catalog when to expect its answer to change.
  const jobs = usePrismImportJobs()
  // What the jobs list says is still going, and which events those jobs are
  // importing. A per-event job carries that one event's Social Tables id, which is
  // what ties a running job back to its row.
  const active = React.useMemo(() => {
    const running = (jobs.data?.jobs ?? []).filter(
      (j) => j.status === 'queued' || j.status === 'running',
    )
    const uids = new Set<string>()
    for (const job of running) for (const id of job.config.eventIds ?? []) uids.add(id)
    return { any: running.length > 0, uids }
  }, [jobs.data])
  const [onlyUnverified, setOnlyUnverified] = React.useState(false)
  const [page, setPage] = React.useState(1)
  // The filter goes to the API with the page, or the "needs attention" tick
  // would filter fifty rows and call that the answer.
  const catalog = usePrismCatalog(active.any, onlyUnverified ? { onlyUnverified: true } : {}, page)
  // Read the catalog once more when the last import finishes.
  //
  // Polling ran only *while* a job was going, which meant it stopped at the very
  // moment the answer changed: the last read happened with the import still in
  // flight, so the row fell back to "Not imported" and stayed there until someone
  // reloaded the page — after the one action whose whole purpose is to change that.
  const qc = useQueryClient()
  const wasRunning = React.useRef(false)
  React.useEffect(() => {
    if (wasRunning.current && !active.any) {
      void qc.invalidateQueries({ queryKey: prismImportKeys.catalogs() })
    }
    wasRunning.current = active.any
  }, [active.any, qc])
  const scan = useScanPrismCatalog()
  const [error, setError] = React.useState<string | null>(null)

  function onScan(): void {
    setError(null)
    scan.mutate(undefined, { onError: (e) => setError(requestErrorMessage(e)) })
  }

  const data = catalog.data
  // Filtered and sliced by the API. `matched` is what the filter matched, which
  // is what the pager reads; `total` stays the whole catalog, because the
  // summary line above the table is about the catalog rather than this query.
  const rows = data?.events ?? []
  const shown = serverPage(page, data?.matched ?? 0, rows.length)

  return (
    <Card asChild className="space-y-3 p-4">
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">Social Tables event catalog</h2>
            <p className="text-xs text-ink-muted">
              The upcoming events Social Tables has listed, kept between scans. Scanning adds what
              is new and leaves everything already on the list — including what you have marked
              verified.
            </p>
          </div>
          <Button type="button" variant="outline" onClick={onScan} disabled={scan.isPending}>
            {scan.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Scan Social Tables
          </Button>
        </div>

        {error ? <FormError>{error}</FormError> : null}
        {scan.data ? (
          <p className="text-sm text-ink-muted">
            Social Tables listed {scan.data.listed} upcoming · {scan.data.added} new to the catalog ·{' '}
            {scan.data.updated} already on it
          </p>
        ) : null}

        {catalog.isPending ? (
          <p className="text-sm text-ink-muted">Loading the catalog…</p>
        ) : catalog.isError ? (
          <FormError>{requestErrorMessage(catalog.error)}</FormError>
        ) : !data || data.total === 0 ? (
          <p className="text-sm text-ink-muted">
            Nothing in the catalog yet. Scan Social Tables to build the list.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-ink-muted">
                {data.total} event{data.total === 1 ? '' : 's'} · {data.importedCount} imported ·{' '}
                {data.verifiedCount} verified
                {data.lastScanAt ? ` · last scan ${formatIsoDate(data.lastScanAt)}` : ''}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={onlyUnverified}
                  onChange={(e) => {
                    setOnlyUnverified(e.target.checked)
                    setPage(1)
                  }}
                />
                Only what still needs attention
              </label>
            </div>
            {rows.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-ink-muted">
                <CheckCircle2 className="h-4 w-4" />
                Every event in the catalog is imported and verified.
              </p>
            ) : (
              <div>
                {/* Paged rather than a 24rem scroll box. The catalog runs to
                    hundreds after a scan, and a bounded scroller inside a page
                    that also scrolls is two scrollbars competing — and it hid how
                    much was left. */}
                <Table
                  footer={
                    <Pagination
                      page={shown.page}
                      pageCount={shown.pageCount}
                      from={shown.from}
                      to={shown.to}
                      total={shown.total}
                      unit="events"
                      onPage={setPage}
                    />
                  }
                >
                  <TableHeader>
                    <TableHead>Event</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableHeader>
                  <TableBody>
                    {rows.map((ev) => (
                      <CatalogRow
                        key={ev.id}
                        ev={ev}
                        importing={active.uids.has(ev.stEventUid)}
                        onJobStarted={onJobStarted}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {data.events.some((ev) => !ev.venueName) ? (
              <p className="text-xs text-ink-muted">
                Where no venue is shown, neither CSE nor Prismm has named one yet — Social Tables
                only says who owns the event, which is a venue for some and a person for others.
                Scanning again names it once Prismm answers.
              </p>
            ) : null}
          </div>
        )}
      </section>
    </Card>
  )
}

/**
 * Temporary organization-admin console for Social Tables (Prism) imports.
 * Start a job, then watch logs until it finishes. Remove when migration is done.
 */
export function PrismImportPage() {
  const jobsQuery = usePrismImportJobs()
  const start = useStartPrismImport()
  const cancel = useCancelPrismImport()
  const { toast } = useToast()
  const [selectedId, setSelectedId] = React.useState<string>('')
  const [eventRefsText, setEventRefsText] = React.useState('')
  const [venueName, setVenueName] = React.useState('')
  const [limit, setLimit] = React.useState('5')
  const [page, setPage] = React.useState('1')
  const [when, setWhen] = React.useState<PrismImportWhen>('upcoming')
  const [allPages, setAllPages] = React.useState(false)
  const [includeNoPlan, setIncludeNoPlan] = React.useState(true)
  const [skipExisting, setSkipExisting] = React.useState(true)
  const [skipPast, setSkipPast] = React.useState(true)
  const [dryRun, setDryRun] = React.useState(true)
  const [forceNew, setForceNew] = React.useState(false)
  const [replaceUntracked, setReplaceUntracked] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!selectedId && jobsQuery.data?.jobs[0]) {
      setSelectedId(jobsQuery.data.jobs[0].id)
    }
  }, [jobsQuery.data, selectedId])

  // A page can hold 50 at most, so that is the page size whatever is typed.
  const pageSizeLabel = String(Math.min(Math.max(Number.parseInt(limit, 10) || 0, 1), 50))
  const usingEventLinks = eventRefsText.trim() !== ''
  const usingVenue = !usingEventLinks && venueName.trim() !== ''
  // When the listing is already Past, skipping past would drop every event.
  // Pasted links have no When, so the checkbox still applies there.
  const skipPastApplies = usingEventLinks || when !== 'past'
  const skipPastEffective = skipPast && skipPastApplies

  // Parsed on every keystroke rather than on submit, so a mistyped link is
  // caught while the address bar is still one tab away.
  const refs = React.useMemo(() => parsePrismEventRefs(eventRefsText), [eventRefsText])

  function onStart(): void {
    setError(null)
    const ids = refs.eventIds
    const venue = venueName.trim()
    const limitN = Number.parseInt(limit, 10)
    if (ids.length === 0 && venue === '' && (!Number.isFinite(limitN) || limitN < 1)) {
      setError('Paste a Social Tables or Prismm event link, type a venue, or set a list limit.')
      return
    }
    if (refs.problems.length > 0) {
      // Importing the readable ones and dropping the rest would look like it
      // worked; the missing events would only surface much later.
      setError('Fix or remove the lines below before starting.')
      return
    }
    if (ids.length === 0 && venue === '' && limitN > 50) {
      // 50 is Social Tables' page ceiling, so a bigger number cannot mean what
      // it looks like. Say what does work instead of forwarding a doomed request.
      setError('A page holds at most 50 events. Use “Every page” to import the whole list, or set Page to step through it.')
      return
    }
    const pageN = Number.parseInt(page, 10)
    let selection: Pick<StartPrismImportInput, 'eventIds' | 'venueName' | 'limit' | 'when' | 'allPages' | 'page'>
    if (ids.length > 0) {
      selection = { eventIds: ids }
    } else if (venue !== '') {
      selection = { venueName: venue, when }
    } else {
      selection = {
        limit: limitN,
        when,
        ...(allPages ? { allPages: true } : {}),
        ...(!allPages && Number.isFinite(pageN) && pageN > 1 ? { page: pageN } : {}),
      }
    }
    const payload = {
      ...selection,
      dryRun,
      forceNewEvent: forceNew,
      skipExisting: skipExisting && !forceNew,
      skipPast: skipPastEffective,
      includeWithoutFloorplan: includeNoPlan,
      ...(replaceUntracked ? { replaceUntrackedPlans: true } : {}),
    }
    // Checked against the same schema the API validates with, so a bad value is a
    // message under the form rather than a 400 and a console error.
    const validated = startPrismImportInputSchema.safeParse(payload)
    if (!validated.success) {
      setError(validated.error.issues.map((issue) => issue.message).join(' '))
      return
    }
    start.mutate(
      validated.data,
      {
        onSuccess: (job) => setSelectedId(job.id),
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader
        title="Prism import"
        description="Temporary Social Tables → CSE bulk import. Requires PRISM_USERNAME / PRISM_PASSWORD on the API and Playwright Chromium on the server."
      />

      <Callout tone="attention" title="Migration tool — not permanent product UI">
        Jobs create venues, spaces, furniture, events, clients, and documents. Client contacts are added as pending
        users <strong>without</strong> sending invite emails. Prefer dry-run first.
      </Callout>

      <SessionPanel />

      <CatalogPanel onJobStarted={setSelectedId} />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card asChild className="space-y-4 p-4">
          <section>
            <h2 className="text-sm font-semibold">Start import</h2>
            <Field
              label="Event links"
              htmlFor="event-refs"
              hint="One per line. Social Tables events and Prismm seating both work — paste the address bar of either."
            >
              <textarea
                id="event-refs"
                value={eventRefsText}
                onChange={(e) => setEventRefsText(e.target.value)}
                rows={4}
                className="flex w-full rounded-md border border-line-strong bg-surface px-3 py-2 font-mono text-sm"
                placeholder={
                  'https://home.socialtables.com/events/3337f7a6-681b-4dfb-b4de-1ce1e3f313d9\nhttps://ops.prismm.com/occasion/3315442/seating'
                }
              />
            </Field>
            {eventRefsText.trim() !== '' ? (
              <div className="space-y-1 text-sm">
                {refs.items.length > 0 ? (
                  <ul className="space-y-0.5 text-ink-muted">
                    {refs.items.map((item) => (
                      <li key={item.encoded} className="font-mono text-xs">
                        {item.label}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {refs.eventIds.length > 0 ? (
                  <p className="text-ink-muted">
                    {refs.eventIds.length} event{refs.eventIds.length === 1 ? '' : 's'} ready.
                  </p>
                ) : null}
                {refs.problems.map((problem) => (
                  <p key={problem.input} className="text-danger-ink">
                    {problem.reason}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-xs text-ink-muted">
                Social Tables looks like{' '}
                <span className="font-mono">home.socialtables.com/events/&lt;uid&gt;</span>
                . Prismm looks like{' '}
                <span className="font-mono">ops.prismm.com/occasion/&lt;id&gt;/seating</span>
                {' '}(or the SAML link from the ST events list).
              </p>
            )}
            {usingEventLinks ? null : (
              <>
                <Field
                  label="Venue"
                  htmlFor="venue-name"
                  hint="Walks the Social Tables list for this venue — the owner or the Prismm place. “River Garden” matches River Garden Weddings & Events. Leave blank to use the list below."
                >
                  <Input
                    id="venue-name"
                    value={venueName}
                    onChange={(e) => setVenueName(e.target.value)}
                    placeholder="River Garden Weddings & Events"
                  />
                </Field>
                {usingVenue ? (
                  <WhenSelect value={when} onChange={setWhen} />
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <Field label="List limit" htmlFor="limit">
                        <Input
                          id="limit"
                          type="number"
                          min={1}
                          max={50}
                          value={limit}
                          onChange={(e) => setLimit(e.target.value)}
                        />
                      </Field>
                      <Field
                        label="Page"
                        htmlFor="page"
                        hint={
                          allPages
                            ? 'Ignored while “Every page” is on.'
                            : 'Page 2 with limit 50 is events 51–100. The log says how many pages there are.'
                        }
                      >
                        <Input
                          id="page"
                          type="number"
                          min={1}
                          value={page}
                          disabled={allPages}
                          onChange={(e) => setPage(e.target.value)}
                        />
                      </Field>
                      <WhenSelect value={when} onChange={setWhen} />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={allPages}
                        onChange={(e) => setAllPages(e.target.checked)}
                      />
                      Every page (walk the whole list, not just one page of {pageSizeLabel})
                    </label>
                  </>
                )}
              </>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeNoPlan}
                onChange={(e) => setIncludeNoPlan(e.target.checked)}
              />
              <span>
                Include events with no floor plan
                <span className="block text-xs text-ink-muted">
                  On so dates, clients and documents still import. They may file under Unassigned
                  if Prism gives no venue — that can’t be changed later. Untick to skip them.
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              Dry run (crawl only — no CSE writes)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={skipExisting}
                disabled={forceNew}
                onChange={(e) => setSkipExisting(e.target.checked)}
              />
              <span>
                Skip events already imported
                <span className="block text-xs text-ink-muted">
                  Same name as a CSE event. A venue re-run only crawls what is new.
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={skipPastEffective}
                disabled={!skipPastApplies}
                onChange={(e) => setSkipPast(e.target.checked)}
              />
              <span>
                Skip past events
                <span className="block text-xs text-ink-muted">
                  Dates before today. Off (or When → Past) if you want last year’s
                  floor plans too.
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={forceNew}
                onChange={(e) => {
                  setForceNew(e.target.checked)
                  if (e.target.checked) setSkipExisting(false)
                }}
              />
              Force new event even if name matches
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={replaceUntracked}
                onChange={(e) => setReplaceUntracked(e.target.checked)}
              />
              <span>
                Replace plans imported before September 28, 2026
                <span className="block text-xs text-ink-muted">
                  Those are normally left alone when they differ from the source, because CSE can’t tell an edit made
                  here from a change in Prismm. Tick this to overwrite them — including anything people changed in
                  CSE. Plans changed since a later import, and plans open right now, are still left alone.
                </span>
              </span>
            </label>
            {error ? <FormError>{error}</FormError> : null}
            <Button pending={start.isPending} type="button" onClick={onStart}>
              <Play className="h-4 w-4" />
              Start job
            </Button>
          </section>
        </Card>

        <Card asChild className="space-y-3 p-4">
          <section>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Jobs</h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void jobsQuery.refetch()}
                disabled={jobsQuery.isFetching}
              >
                <RefreshCw className={`h-4 w-4 ${jobsQuery.isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {jobsQuery.isPending ? (
              <p className="text-sm text-ink-muted">Loading…</p>
            ) : jobsQuery.isError ? (
              <FormError>{requestErrorMessage(jobsQuery.error)}</FormError>
            ) : (jobsQuery.data?.jobs.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-muted">No jobs yet.</p>
            ) : (
              <div className="max-h-64 space-y-1 overflow-auto">
                {jobsQuery.data?.jobs.map((j) => (
                  <JobRow
                    key={j.id}
                    job={j}
                    selected={j.id === selectedId}
                    onSelect={() => setSelectedId(j.id)}
                    canceling={cancel.isPending && cancel.variables === j.id}
                    onCancel={() => {
                      cancel.mutate(j.id, {
                        onError: (e) => toast(requestErrorMessage(e), { variant: 'error' }),
                      })
                    }}
                  />
                ))}
              </div>
            )}
          </section>
        </Card>
      </div>

      {selectedId ? (
        <Card asChild className="space-y-2 p-4">
          <section>
            <h2 className="text-sm font-semibold">Job log</h2>
            <JobLog jobId={selectedId} />
          </section>
        </Card>
      ) : null}

      <ResetPanel />
    </PageContainer>
  )
}
